import {
    monitoringAccountList,
    monitoringBettingList,
    monitoringBookmakerList,
    monitoringProxyList,
    monitoringServerTime,
    monitoringSettlementList,
} from '@/api/monitoring';
import { MonitoringAccount, MonitoringBetting, MonitoringBookmaker, MonitoringProxy, MonitoringSettlement } from '@/models/monitoring';
import ErrorService from '@/services/errorService';

export const useMonitoringStore = defineStore('monitoring', () => {
    const bookmakers = ref<MonitoringBookmaker[]>([]);
    const proxies = ref<MonitoringProxy[]>([]);
    const accounts = ref<MonitoringAccount[]>([]);
    const betting = ref<MonitoringBetting[]>([]);
    const settlement = ref<MonitoringSettlement[]>([]);
    const serverTime = ref<string>('');
    const serverTimeDate = ref<Date | null>(null);

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let serverTimeIntervalId: ReturnType<typeof setInterval> | null = null;
    const POLLING_INTERVAL = 5000; // 5 seconds
    const SERVER_TIME_UPDATE_INTERVAL = 1000; // 1 second

    const loadBookmakers = async () => {
        try {
            const response = await monitoringBookmakerList();
            const data = response.data.data || response.data.rows || [];
            bookmakers.value = data.map((item) => new MonitoringBookmaker(item));
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const loadProxies = async () => {
        try {
            const response = await monitoringProxyList();
            const data = response.data.data || response.data.rows || [];
            proxies.value = data.map((item) => new MonitoringProxy(item));
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const loadAccounts = async () => {
        try {
            const response = await monitoringAccountList();
            const data = response.data.data || response.data.rows || [];
            accounts.value = data.map((item) => new MonitoringAccount(item));
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const loadBetting = async () => {
        try {
            const response = await monitoringBettingList();
            const data = response.data.data || response.data.rows || [];
            betting.value = data.map((item) => new MonitoringBetting(item));
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const loadSettlement = async () => {
        try {
            const response = await monitoringSettlementList();
            const data = response.data.data || response.data.rows || [];
            settlement.value = data.map((item) => new MonitoringSettlement(item));
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const loadServerTime = async () => {
        try {
            const response = await monitoringServerTime();
            const timeString = response.data.serverTime || '';
            if (timeString) {
                // Parse server time string to Date object
                // Format: "2026 02 23 19:09:20" or similar
                const timeMatch = timeString.match(/(\d{4})\s+(\d{2})\s+(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
                if (timeMatch) {
                    const [, year, month, day, hour, minute, second] = timeMatch;
                    serverTimeDate.value = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
                    serverTime.value = timeString;
                } else {
                    // Try to parse as ISO string or other format
                    const parsedDate = new Date(timeString);
                    if (!isNaN(parsedDate.getTime())) {
                        serverTimeDate.value = parsedDate;
                        serverTime.value = timeString;
                    }
                }
            }
        } catch (error) {
            ErrorService.log(error);
        }
    };

    const updateServerTimeDisplay = () => {
        if (serverTimeDate.value) {
            const now = new Date(serverTimeDate.value);
            now.setSeconds(now.getSeconds() + 1);
            serverTimeDate.value = now;

            // Format: "YYYY MM DD HH:mm:ss" (with spaces between all numbers)
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            serverTime.value = `${year} ${month} ${day} ${hours}:${minutes}:${seconds}`;
        }
    };

    const loadAll = async () => {
        await Promise.all([loadBookmakers(), loadProxies(), loadAccounts(), loadBetting(), loadSettlement()]);
    };

    const startPolling = () => {
        if (intervalId) {
            clearInterval(intervalId);
        }

        // Load immediately
        loadAll();

        // Then poll every 5 seconds
        intervalId = setInterval(() => {
            loadAll();
        }, POLLING_INTERVAL);
    };

    const stopPolling = () => {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    };

    const startServerTimeUpdate = async () => {
        // Load server time once
        await loadServerTime();

        // Then update every second
        if (serverTimeIntervalId) {
            clearInterval(serverTimeIntervalId);
        }

        serverTimeIntervalId = setInterval(() => {
            updateServerTimeDisplay();
        }, SERVER_TIME_UPDATE_INTERVAL);
    };

    const stopServerTimeUpdate = () => {
        if (serverTimeIntervalId) {
            clearInterval(serverTimeIntervalId);
            serverTimeIntervalId = null;
        }
    };

    return {
        bookmakers,
        proxies,
        accounts,
        betting,
        settlement,
        serverTime,
        loadAll,
        startPolling,
        stopPolling,
        startServerTimeUpdate,
        stopServerTimeUpdate,
    };
});
